import type { NotificationContent } from "../../../components/notifications/notification-content.types"
import { notificationHref } from "./notificationHref"
import type { Notification } from "../types/notification.types"

/**
 * What a notification states when the backend did not send a content block.
 *
 * This is the deploy-window case — the five repos ship separately, so a web
 * build can meet a backend that predates `content`. It is not a second
 * content model: it reads only what the old payload genuinely carried (the
 * record's name from `data`, the destination `notificationHref` already
 * resolved) and invents nothing. A row with none of that falls through to its
 * own `description`, which is what the card renders when `record` and
 * `subject` are both absent.
 */
export function legacyContent(notification?: Notification | null): NotificationContent {
  const data = notification?.data ?? {}
  const href = notification ? notificationHref(notification) : null

  const projectId = data.project_id
  const leadId = data.lead_id
  const recordId = projectId ?? leadId ?? notification?.target?.id
  const recordName = data.project_name ?? data.lead_name ?? notification?.target?.name

  return {
    // Never the actor: "EzBuild" as a headline says nothing about what
    // happened. The stored verb is the closest thing an old row has to one.
    title: notification?.verb || notification?.description || "",
    category: "project",
    record:
      recordId && recordName
        ? {
            kind: projectId || data.is_project ? "project" : "lead",
            id: String(recordId),
            name: String(recordName)
          }
        : null,
    customer: null,
    subject: data.file_name
      ? { kind: "document", id: data.attachment_id ?? null, label: String(data.file_name) }
      : null,
    facts: [],
    // `href` is already a resolved path, so it rides the target's `url`-free
    // shape as a pre-built destination the card can open.
    action: href ? { key: "open_record", target: { kind: "legacy_href", url: href } } : null,
    actor: null,
    due_date: null,
    branch: null,
    resolved: Boolean(recordId)
  } as NotificationContent
}
